import React, { useState, useEffect, useRef } from 'react'
import { Button } from '../../../Button'
import { cn } from '../../../../../lib/utils'
import { Icon } from '../../../Icon'
import {
  Select as SelectP,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../Select/primative'

export interface ModelOption {
  value: string
  label: string
  providerName: string
}

export interface SuggestedResponseProps {
  message: string
  models: ModelOption[]
  selectedModelId: string | null
  onModelChange: (modelId: string) => void | Promise<void>
  onSend: (message: string) => void | Promise<void>
  onDismiss: () => void | Promise<void>
  onEdit: (message: string) => void | Promise<void>
  onCopy: (message: string) => void | Promise<void>
  onRegenerate: () => void | Promise<void>
  isDisabled?: boolean
  isLoading?: boolean
  error?: string | null
  className?: string
  compact?: boolean
}

export default function SuggestedResponse(props: SuggestedResponseProps) {
  const {
    message,
    models,
    selectedModelId,
    onModelChange,
    onSend,
    onDismiss,
    onEdit,
    onCopy,
    onRegenerate,
    isDisabled = false,
    isLoading = false,
    error,
    className,
    compact = false,
  } = props

  const [isEditing, setIsEditing] = useState(false)
  const [editedMessage, setEditedMessage] = useState(message)
  const [copied, setCopied] = useState(false)
  const [hasReceivedContent, setHasReceivedContent] = useState(false)
  const prevMessageRef = useRef(message)

  useEffect(() => {
    setEditedMessage(message)
    if (message && !prevMessageRef.current) {
      setHasReceivedContent(true)
    }
    if (!message && !isLoading) {
      setHasReceivedContent(false)
    }
    prevMessageRef.current = message
  }, [message, isLoading])

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setEditedMessage(newValue)
    onEdit(newValue)
  }

  const handleSend = async () => {
    await onSend(editedMessage)
  }

  const handleDismiss = async () => {
    await onDismiss()
  }

  const handleCopy = async () => {
    await onCopy(editedMessage)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleModelChange = async (modelId: string) => {
    await onModelChange(modelId)
  }

  const groupedModels = models.reduce(
    (acc, model) => {
      if (!acc[model.providerName]) {
        acc[model.providerName] = []
      }
      acc[model.providerName]!.push(model)
      return acc
    },
    {} as Record<string, ModelOption[]>,
  )

  const isInitialLoading =
    isLoading && !message && !hasReceivedContent && !error
  const showFullView = !isInitialLoading

  if (isInitialLoading) {
    return (
      <div
        className={cn(
          'w-full rounded-lg bg-muted/50 border border-dashed border-primary/30',
          'p-3 transition-all duration-300 ease-out',
          className,
        )}
      >
        <div className="flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <Icon name="Sparkles" className="h-4 w-4 text-primary" />
          <span className="text-sm text-muted-foreground">
            Generating response
          </span>
          <Icon
            name="LoaderCircle"
            className="h-4 w-4 animate-spin text-muted-foreground ml-auto"
          />
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'w-full rounded-lg bg-muted/50 border border-dashed border-primary/30',
        'transition-all duration-300 ease-out',
        compact ? 'p-2 space-y-2' : 'p-4 space-y-3',
        showFullView &&
          'animate-in fade-in slide-in-from-bottom-2 duration-300',
        className,
      )}
    >
      {!compact && (
        <div className="flex items-center justify-between gap-2 animate-in fade-in duration-200">
          <div className="flex items-center gap-2">
            <Icon name="Sparkles" className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-muted-foreground">
              Suggested Response
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isLoading && (
              <Icon
                name="LoaderCircle"
                className="h-4 w-4 animate-spin text-muted-foreground"
              />
            )}
            <SelectP
              value={selectedModelId || ''}
              onValueChange={handleModelChange}
              disabled={isLoading}
            >
              <SelectTrigger className="h-7 w-[180px] text-xs">
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(groupedModels).map(
                  ([providerName, providerModels]) => (
                    <div key={providerName}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                        {providerName}
                      </div>
                      {providerModels.map((model) => (
                        <SelectItem
                          key={model.value}
                          value={model.value}
                          className="text-xs"
                        >
                          {model.label}
                        </SelectItem>
                      ))}
                    </div>
                  ),
                )}
              </SelectContent>
            </SelectP>
          </div>
        </div>
      )}

      <div className="space-y-2 animate-in fade-in duration-200 delay-75">
        {error ? (
          <div
            className={cn(
              'rounded-md bg-destructive/10 text-destructive text-sm',
              compact ? 'p-2' : 'p-3',
            )}
          >
            {error}
          </div>
        ) : isEditing ? (
          <textarea
            value={editedMessage}
            onChange={handleMessageChange}
            className={cn(
              'w-full rounded-md border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring',
              compact ? 'min-h-[60px] p-2' : 'min-h-[80px] p-3',
            )}
            placeholder="Edit your message..."
            disabled={isDisabled || isLoading}
            autoFocus
          />
        ) : (
          <div
            className={cn(
              'rounded-md bg-background/50 text-sm whitespace-pre-wrap transition-colors',
              compact ? 'p-2 min-h-[40px]' : 'p-3 min-h-[60px]',
              !isLoading && 'cursor-pointer hover:bg-background/70',
              isLoading && !message && 'animate-pulse',
            )}
            onClick={!isLoading ? handleEdit : undefined}
          >
            {message || (isLoading ? 'Generating response...' : '')}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between animate-in fade-in duration-200 delay-150">
        {!compact && (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              handleClick={handleCopy}
              isDisabled={isDisabled || isLoading || !message}
              label={copied ? 'Copied!' : 'Copy'}
              leftIcon={copied ? 'Check' : 'Copy'}
            />
            <Button
              variant="ghost"
              size="sm"
              handleClick={onRegenerate}
              isDisabled={isDisabled || isLoading}
              label="Regenerate"
              leftIcon="RefreshCw"
            />
          </div>
        )}
        <div
          className={cn(
            'flex items-center gap-2',
            compact && 'w-full justify-between',
          )}
        >
          {compact && isLoading && (
            <Icon
              name="LoaderCircle"
              className="h-4 w-4 animate-spin text-muted-foreground"
            />
          )}
          {compact && !isLoading && <div />}
          <div className="flex items-center gap-2">
            {compact && (
              <SelectP
                value={selectedModelId || ''}
                onValueChange={handleModelChange}
                disabled={isLoading}
              >
                <SelectTrigger className="h-6 w-[140px] text-xs">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(groupedModels).map(
                    ([providerName, providerModels]) => (
                      <div key={providerName}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                          {providerName}
                        </div>
                        {providerModels.map((model) => (
                          <SelectItem
                            key={model.value}
                            value={model.value}
                            className="text-xs"
                          >
                            {model.label}
                          </SelectItem>
                        ))}
                      </div>
                    ),
                  )}
                </SelectContent>
              </SelectP>
            )}
            <Button
              variant="outline"
              size="sm"
              handleClick={handleDismiss}
              isDisabled={isDisabled || isLoading}
              label={compact ? undefined : 'Dismiss'}
              leftIcon="X"
            />
            <Button
              variant="default"
              size="sm"
              handleClick={handleSend}
              isDisabled={isDisabled || isLoading || !message}
              label={compact ? undefined : 'Send'}
              leftIcon="Send"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
